import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  isAppleReviewRoute,
  shouldShowEmailSignInOnAuthScreen,
} from '@/lib/reviewer-auth';

interface EmailSignInFormProps {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
  loading: boolean;
  /** Dark auth card (/auth) — readable on slate-900 */
  variant?: 'default' | 'authDark';
}

const EmailSignInForm = ({
  email,
  setEmail,
  password,
  setPassword,
  onSubmit,
  loading,
  variant = 'default',
}: EmailSignInFormProps) => {
  const isDark = variant === 'authDark';
  const labelClass = isDark ? 'text-sm font-medium text-slate-300' : 'text-sm font-medium';
  const inputClass = isDark
    ? 'bg-slate-800/90 border-slate-600 text-white placeholder:text-slate-500'
    : undefined;

  const showReviewerHints = isAppleReviewRoute() || shouldShowEmailSignInOnAuthScreen();

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-1">
        <label className={labelClass}>Email</label>
        <Input
          type="email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          placeholder={showReviewerHints ? 'App Review account email' : 'your@email.com'}
          required
          autoComplete="email"
          className={inputClass}
        />
      </div>

      <div className="space-y-1">
        <label className={labelClass}>Password</label>
        <Input
          type="password"
          value={password}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
          autoComplete="current-password"
          className={inputClass}
        />
        {showReviewerHints && (
          <p
            className={
              isDark
                ? 'text-xs text-slate-400 mt-1.5'
                : 'text-xs text-muted-foreground mt-1.5'
            }
            role="note"
          >
            For App Review: use the credentials provided in your review notes.
          </p>
        )}
      </div>

      <Button
        type="submit"
        className={
          isDark
            ? 'w-full bg-slate-700 text-white hover:bg-slate-600 border border-slate-500'
            : 'w-full'
        }
        disabled={loading}
      >
        {loading ? 'Signing In...' : 'Sign In'}
      </Button>
    </form>
  );
};

export default EmailSignInForm;
